import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useId,
} from "react";
import styles from "../../styles/components/filters/CategoryFilters.module.css";
import { Range, getTrackBackground } from "react-range";
import { FaSearch } from "react-icons/fa";
import useDebounce from "../../hooks/useDebounce";
import capitalizeFirst from "../../utils/capitalizeFirst";
import {
  formatCompareFieldValue,
  parseBooleanValue,
} from "../../utils/compareFieldUtils";
import api from "../../utils/api/axiosInstance";
import { useToast } from "../../contexts/ToastContext";

const STEP = 100;
const PRICE_DEBOUNCE_MS = 300;
const FETCH_TIMEOUT_MS = 5000;
const DEFAULT_MAX_PRICE_FALLBACK = 100000;
const MAX_VISIBLE_SUGGESTIONS = 50;

const getSuggestionLabel = (item) => {
  if (typeof item === "object") {
    return item.name ?? "";
  }
  return String(item);
};

const sortSuggestionsAlphabetically = (items) =>
  [...items].sort((a, b) =>
    getSuggestionLabel(a).localeCompare(getSuggestionLabel(b), undefined, {
      sensitivity: "base",
      numeric: true,
    }),
  );

const readPriceFromFilters = (filterState, fallbackMax) => {
  const min = Number(filterState.minPrice) || 0;
  const max =
    filterState.maxPrice !== "" && filterState.maxPrice != null
      ? Number(filterState.maxPrice)
      : fallbackMax;
  return [min, max];
};

const CategoryFilters = ({
  filters,
  setFilters,
  dynamicFields = [],
  categoryId,
  onReset,
  seedMaxPrice = 0,
}) => {
  const { showToast } = useToast();
  const idPrefix = useId();
  const seededMax =
    seedMaxPrice > 0 ? Math.max(1000, seedMaxPrice) : DEFAULT_MAX_PRICE_FALLBACK;
  const [maxPriceLimit, setMaxPriceLimit] = useState(seededMax);
  const [priceRange, setPriceRange] = useState(() =>
    readPriceFromFilters(filters, seededMax),
  );
  const [priceReady, setPriceReady] = useState(seedMaxPrice > 0);
  const prevOnResetRef = useRef(onReset);
  const [brandOptions, setBrandOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
  const [customFieldOptions, setCustomFieldOptions] = useState({});
  const debouncedPriceRange = useDebounce(priceRange, PRICE_DEBOUNCE_MS);

  // Update a single filter key
  const updateFilter = useCallback(
    (name, val) => {
      setFilters((prev) => ({ ...prev, [name]: val }));
    },
    [setFilters]
  );

  // Fetch max price for the category and initialize the slider
  const fetchMaxPrice = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

      const { data } = await api.get(
        `/products/max-price/${encodeURIComponent(categoryId)}`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);
      const max = Number(data.maxPrice) || 0;
      const rounded = max > 0 ? Math.ceil(max / 1000) * 1000 : 1000;
      setPriceRange([0, rounded]);
      setMaxPriceLimit(rounded);
      setPriceReady(true);
    } catch (err) {
      console.error("Failed to fetch max price:", err.response?.data || err.message);
      // Always fall back to a usable default so the slider isn't stuck unresponsive
      setPriceRange([0, DEFAULT_MAX_PRICE_FALLBACK]);
      setMaxPriceLimit(DEFAULT_MAX_PRICE_FALLBACK);
      setPriceReady(true);
      showToast({
        message: "Couldn't load the price range. Using a default range instead.",
        type: "error",
      });
    }
  }, [categoryId, showToast]);

  // Reset range only when parent explicitly triggers onReset (not when max limit loads)
  useEffect(() => {
    if (prevOnResetRef.current === onReset) return;
    prevOnResetRef.current = onReset;
    setPriceRange([0, maxPriceLimit]);
  }, [onReset, maxPriceLimit]);

  // Update slider ceiling when category max price is known; preserve selected max
  useEffect(() => {
    if (!seedMaxPrice) return;
    const rounded = Math.max(1000, seedMaxPrice);
    setMaxPriceLimit(rounded);
    setPriceRange((prev) => [
      Math.min(prev[0], rounded),
      Math.min(prev[1], rounded),
    ]);
    setPriceReady(true);
  }, [seedMaxPrice]);

  // Keep slider in sync when parent filter state changes (URL seed, reset, etc.)
  useEffect(() => {
    const [min, max] = readPriceFromFilters(filters, maxPriceLimit);
    const clampedMax = Math.min(max, maxPriceLimit);
    setPriceRange((prev) => {
      if (prev[0] === min && prev[1] === clampedMax) return prev;
      return [min, clampedMax];
    });
  }, [filters.minPrice, filters.maxPrice, maxPriceLimit]);

  // Load max price on category change when parent has not seeded it yet
  useEffect(() => {
    if (seedMaxPrice) return;
    setPriceReady(false);
    if (categoryId) fetchMaxPrice();
  }, [categoryId, fetchMaxPrice, seedMaxPrice]);

  // Push debounced slider values into external filters (only after max price is known)
  useEffect(() => {
    if (!priceReady) return;
    setFilters((prev) => {
      const [min, max] = debouncedPriceRange;
      if (max > maxPriceLimit) return prev;
      if (prev.minPrice === min && prev.maxPrice === max) return prev;
      return { ...prev, minPrice: min, maxPrice: max };
    });
  }, [debouncedPriceRange, setFilters, priceReady, maxPriceLimit]);

  const commitPriceRange = useCallback(() => {
    if (!priceReady) return;
    setFilters((prev) => {
      const min = Math.max(0, Math.min(priceRange[0], maxPriceLimit));
      const max = Math.max(0, Math.min(priceRange[1], maxPriceLimit));
      if (prev.minPrice === min && prev.maxPrice === max) return prev;
      return { ...prev, minPrice: min, maxPrice: max };
    });
  }, [priceRange, priceReady, maxPriceLimit, setFilters]);

  // Fetch field options (brand/model/year + dynamic fields)
  // Note: dynamicFields is expected to be referentially stable per category from the
  // parent (derived once from category data), so this only re-runs on real category changes.
  useEffect(() => {
    let cancelled = false;
    let hadFailure = false;

    const fetchFieldOptions = async (field) => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

        const res = await api.get(
          `/products/${encodeURIComponent(categoryId)}/field/${encodeURIComponent(
            field,
          )}`,
          { signal: controller.signal },
        );

        clearTimeout(timeoutId);
        return res.data || [];
      } catch (err) {
        console.error(`Failed to fetch ${field} options:`, err.response?.data || err.message);
        hadFailure = true;
        return [];
      }
    };

    const loadData = async () => {
      const [brands, models, years] = await Promise.all([
        fetchFieldOptions("brand"),
        fetchFieldOptions("model"),
        fetchFieldOptions("year"),
      ]);
      if (cancelled) return;
      setBrandOptions(brands);
      setModelOptions(models);
      setYearOptions(years);

      if (dynamicFields.length > 0) {
        const customFieldsArray = await Promise.all(
          dynamicFields.map(async (field) => {
            const options = await fetchFieldOptions(field.name.trim());
            return { [field.name.trim()]: options };
          })
        );
        if (cancelled) return;
        const allCustomFields = customFieldsArray.reduce(
          (acc, curr) => ({ ...acc, ...curr }),
          {}
        );
        setCustomFieldOptions(allCustomFields);
      }

      if (hadFailure && !cancelled) {
        showToast({
          message: "Some filter options couldn't be loaded.",
          type: "error",
        });
      }
    };

    if (categoryId) loadData();

    return () => {
      cancelled = true;
    };
  }, [categoryId, dynamicFields, showToast]);

  // Strip unsupported prop for react-range render props
  const cleanProps = ({ key, ...safeProps }) => safeProps;

  // Defensive check - don't render if categoryId is missing
  if (!categoryId) {
    return (
      <div className={styles.categoryFiltersContainer}>
        <p>Loading filters...</p>
      </div>
    );
  }

  const minPriceInputId = `${idPrefix}-min-price`;
  const maxPriceInputId = `${idPrefix}-max-price`;

  return (
    <div className={styles.categoryFiltersContainer}>
      <div className={styles.categoryFiltersInputWrapper}>
        <label className={styles.filterLabel} id={`${idPrefix}-price-label`}>
          Price Range
        </label>
        <Range
          step={STEP}
          min={0}
          max={maxPriceLimit}
          values={priceRange}
          onChange={(values) => setPriceRange(values)}
          renderTrack={({ props, children }) => (
            <div
              {...cleanProps(props)}
              className={styles.rangeTrack}
              role="group"
              aria-labelledby={`${idPrefix}-price-label`}
              style={{
                ...props.style,
                "--track-bg": getTrackBackground({
                  values: priceRange,
                  colors: ["var(--color-border)", "var(--color-primary)", "var(--color-border)"],
                  min: 0,
                  max: maxPriceLimit,
                }),
              }}
            >
              {children}
            </div>
          )}
          renderThumb={({ props, isDragged, index }) => (
            <div
              key={index}
              {...cleanProps(props)}
              className={`${styles.rangeThumb} ${
                isDragged ? styles.rangeThumbDragging : ""
              }`}
              aria-label={index === 0 ? "Minimum price" : "Maximum price"}
            />
          )}
        />

        <div className={styles.priceLabels}>
          <div>
            <label htmlFor={minPriceInputId}>Min:</label>
            <input
              id={minPriceInputId}
              type="text"
              inputMode="numeric"
              name="min"
              value={Number(priceRange[0]).toLocaleString()}
              onChange={(e) => {
                const newMin = Number(String(e.target.value).replace(/,/g, "")) || 0;
                setPriceRange([Math.max(0, Math.min(newMin, maxPriceLimit)), priceRange[1]]);
              }}
              onBlur={commitPriceRange}
            />
          </div>
          <div>
            <label htmlFor={maxPriceInputId}>Max:</label>
            <input
              id={maxPriceInputId}
              type="text"
              inputMode="numeric"
              name="max"
              value={Number(priceRange[1]).toLocaleString()}
              onChange={(e) => {
                const newMax = Number(String(e.target.value).replace(/,/g, "")) || 0;
                setPriceRange([priceRange[0], Math.max(0, Math.min(newMax, maxPriceLimit))]);
              }}
              onBlur={commitPriceRange}
            />
          </div>
        </div>

        {/* Unified Input Block */}
        <AutocompleteFilter
          name="brand"
          value={filters.brand || []}
          onChange={updateFilter}
          suggestions={brandOptions}
          placeholder="Brand"
          onReset={onReset}
        />
        <AutocompleteFilter
          name="model"
          value={filters.model || []}
          onChange={updateFilter}
          suggestions={modelOptions}
          placeholder="Model"
          onReset={onReset}
        />
        <AutocompleteFilter
          name="year"
          value={filters.year || []}
          onChange={updateFilter}
          suggestions={yearOptions}
          placeholder="Year"
          onReset={onReset}
        />

        {dynamicFields
          .filter(
            (field) =>
              !["brand", "model", "year"].includes(field.name?.trim().toLowerCase())
          )
          .map((field) => (
            <AutocompleteFilter
              key={field.name}
              name={field.name}
              value={filters[field.name] || []}
              onChange={updateFilter}
              suggestions={customFieldOptions[field.name] || []}
              placeholder={field.label}
              onReset={onReset}
              fieldType={field.type}
            />
          ))}
      </div>
    </div>
  );
};

export default CategoryFilters;

// 🔽 Inline Autocomplete Filter Component (included inside the same file)
const AutocompleteFilter = ({
  name,
  value,
  onChange,
  suggestions,
  placeholder,
  onReset = 0,
  fieldType,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const prevOnResetRef = useRef(onReset);
  const instanceId = useId();
  const listboxId = `${instanceId}-listbox`;
  const getOptionId = (i) => `${instanceId}-option-${i}`;

  const getItemLabel = getSuggestionLabel;

  const getItemValue = (item) => {
    if (typeof item === "object") {
      return item._id ?? item.name ?? "";
    }
    return item;
  };

  const resolveFilterLabel = useCallback(
    (item) => {
      if (fieldType === "checkbox") {
        const formatted = formatCompareFieldValue(item, { type: "checkbox" });
        return formatted === "—" ? "" : formatted;
      }
      return getItemLabel(item);
    },
    [fieldType],
  );

  const resolveFilterValue = useCallback(
    (item) => {
      if (fieldType === "checkbox") {
        const raw =
          typeof item === "object" ? item.name ?? item._id ?? "" : item;
        const bool = parseBooleanValue(raw);
        if (bool === true) return "Yes";
        if (bool === false) return "No";
        return raw;
      }
      return getItemValue(item);
    },
    [fieldType],
  );

  const filteredSuggestions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const validSuggestions = (suggestions || []).filter((s) => {
      const label = resolveFilterLabel(s);
      return label !== undefined && label !== null && String(label).trim() !== "";
    });

    let items;
    if (term.length < 1) {
      items = isOpen ? validSuggestions.slice(0, MAX_VISIBLE_SUGGESTIONS) : [];
    } else {
      items = validSuggestions.filter((s) =>
        String(resolveFilterLabel(s)).toLowerCase().includes(term),
      );
    }

    return sortSuggestionsAlphabetically(items);
  }, [isOpen, searchTerm, suggestions, resolveFilterLabel]);

  // Map selected values back to their display labels for chip rendering
  const selectedItems = useMemo(() => {
    if (!Array.isArray(value) || value.length === 0) return [];
    return value.map((val) => {
      const normalizedVal = resolveFilterValue(val);
      const found = (suggestions || []).find(
        (s) => resolveFilterValue(s) === normalizedVal,
      );
      return {
        value: normalizedVal,
        label: found
          ? capitalizeFirst(resolveFilterLabel(found))
          : capitalizeFirst(String(normalizedVal)),
      };
    });
  }, [value, suggestions, resolveFilterLabel, resolveFilterValue]);

  const handleToggle = (item) => {
    const val = resolveFilterValue(item);
    const current = Array.isArray(value) ? value.map((v) => resolveFilterValue(v)) : [];
    const updated = current.includes(val)
      ? current.filter((v) => v !== val)
      : [...current, val];
    onChange(name, updated);
  };

  const handleRemoveChip = (val) => {
    const current = Array.isArray(value) ? value : [];
    onChange(name, current.filter((v) => v !== val));
  };

  const commitSearchTerm = useCallback(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return;

    const current = Array.isArray(value)
      ? value.map((v) => resolveFilterValue(v))
      : [];

    let match = filteredSuggestions.find(
      (item) => String(resolveFilterLabel(item)).toLowerCase() === term,
    );
    if (!match && filteredSuggestions.length === 1) {
      match = filteredSuggestions[0];
    }
    if (!match) return;

    const val = resolveFilterValue(match);
    if (!current.includes(val)) {
      onChange(name, [...current, val]);
    }
    setSearchTerm("");
  }, [
    searchTerm,
    filteredSuggestions,
    value,
    name,
    onChange,
    resolveFilterLabel,
    resolveFilterValue,
  ]);

  // Keep the highlighted option within bounds whenever the visible options change
  useEffect(() => {
    setHighlightedIndex((prev) =>
      filteredSuggestions.length === 0
        ? -1
        : Math.min(Math.max(prev, 0), filteredSuggestions.length - 1),
    );
  }, [filteredSuggestions.length]);

  // Clear leftover search text when parent resets filters
  useEffect(() => {
    if (prevOnResetRef.current === onReset) return;
    prevOnResetRef.current = onReset;
    setSearchTerm("");
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, [onReset]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (
        isOpen &&
        highlightedIndex >= 0 &&
        filteredSuggestions[highlightedIndex]
      ) {
        e.preventDefault();
        handleToggle(filteredSuggestions[highlightedIndex]);
        setSearchTerm("");
      } else if (searchTerm.trim()) {
        e.preventDefault();
        commitSearchTerm();
      }
      return;
    }

    if (!isOpen) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        filteredSuggestions.length === 0
          ? -1
          : (prev + 1) % filteredSuggestions.length,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        filteredSuggestions.length === 0
          ? -1
          : (prev - 1 + filteredSuggestions.length) % filteredSuggestions.length,
      );
    } else if (e.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={styles.autocompleteWrapper}
      onFocusCapture={() => setIsOpen(true)}
      onBlurCapture={(e) => {
        if (!wrapperRef.current?.contains(e.relatedTarget)) {
          commitSearchTerm();
          setIsOpen(false);
        }
      }}
    >
      {/* Header: filter label + active count badge */}
      <div className={styles.filterHeader}>
        <span className={styles.filterHeaderLabel}>{placeholder}</span>
        {selectedItems.length > 0 && (
          <span className={styles.filterBadge}>{selectedItems.length}</span>
        )}
      </div>

      {/* Selected chips – always visible so the user can see active filters */}
      {selectedItems.length > 0 && (
        <div className={styles.selectedChips}>
          {selectedItems.map(({ value: val, label }) => (
            <span key={val} className={styles.selectedChip}>
              {label}
              <button
                type="button"
                className={styles.chipRemoveBtn}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleRemoveChip(val)}
                aria-label={`Remove ${label}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className={styles.searchInputWrapper}>
        <FaSearch className={styles.searchIcon} />
        <input
          className={styles.searchInput}
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            highlightedIndex >= 0 ? getOptionId(highlightedIndex) : undefined
          }
          value={searchTerm}
          onChange={(e) => {
            const next = e.target.value;
            setSearchTerm(next);
          }}
          onKeyDown={handleKeyDown}
          placeholder={`Search ${placeholder}`}
          name={name}
          autoComplete="off"
        />
      </div>

      {isOpen && (
        <ul className={styles.autocompleteList} role="listbox" id={listboxId}>
          {filteredSuggestions.length > 0 ? (
            filteredSuggestions.map((item, i) => {
              const label = capitalizeFirst(resolveFilterLabel(item));
              const itemValue = resolveFilterValue(item);
              const isChecked =
                Array.isArray(value) &&
                value.map((v) => resolveFilterValue(v)).includes(itemValue);
              const isHighlighted = i === highlightedIndex;
              return (
                <li
                  key={`${name}-${i}`}
                  id={getOptionId(i)}
                  className={`${styles.autocompleteItem} ${isChecked ? styles.autocompleteItemChecked : ""} ${isHighlighted ? styles.autocompleteItemHighlighted : ""}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleToggle(item)}
                  onMouseEnter={() => setHighlightedIndex(i)}
                  role="option"
                  aria-selected={isChecked}
                >
                  <span className={styles.autocompleteOption}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      readOnly
                      tabIndex={-1}
                      aria-hidden="true"
                    />
                    <span>{label || "Unknown"}</span>
                  </span>
                </li>
              );
            })
          ) : searchTerm.trim().length >= 1 ? (
            <li className={styles.autocompleteItem} role="option" aria-disabled="true">
              <span className={styles.autocompleteEmptyMessage}>No matches found</span>
            </li>
          ) : (
            <li className={styles.autocompleteItem} role="option" aria-disabled="true">
              <span className={styles.autocompleteEmptyMessage}>No options available</span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};
